import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, Alert, Spinner, Form, Pagination } from 'react-bootstrap';
import { useFileChanges } from '../../services/api/hooks/useFileChanges';
import { useAuth } from '../../contexts/AuthContext';

const FileChanges: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { data: changes, pagination, loading, error, fetchFileChanges, downloadFile } = useFileChanges();

  useEffect(() => {
    fetchFileChanges(currentPage, filter);
  }, [fetchFileChanges, currentPage, filter]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setCurrentPage(1);
      fetchFileChanges(1, filter);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filter, fetchFileChanges]);

  if (!currentUser?.isAdmin) {
    return (
      <Card className="m-3">
        <Card.Header as="h5" style={{ backgroundColor: '#ffcdd2' }}>Access Denied</Card.Header>
        <Card.Body>
          <Alert variant="warning">
            You must be an administrator to access mail server configuration.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  const getStatusBadge = (status: string) => {
    const variants = {
      added: 'success',
      modified: 'warning',
      deleted: 'danger'
    };
    return <Badge bg={variants[status as keyof typeof variants] || 'secondary'}>{status}</Badge>;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const handleDownload = async (fileId: string, filename: string) => {
    try {
      await downloadFile(fileId, filename);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  if (loading) {
    return (
      <Card className="m-3">
        <Card.Header as="h5">Mail Server Config</Card.Header>
        <Card.Body className="text-center">
          <Spinner animation="border" />
          <p className="mt-2">Loading config files...</p>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="m-3">
        <Card.Header as="h5">Mail Server Config</Card.Header>
        <Card.Body>
          <Alert variant="danger">
            Error: {error}
            <Button variant="outline-danger" size="sm" className="ms-2" onClick={() => fetchFileChanges(1, filter)}>
              Retry
            </Button>
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="m-3">
      <Card.Header as="h5" className="d-flex justify-content-between align-items-center">
        Mail Server Config
        <Button variant="outline-primary" size="sm" onClick={() => fetchFileChanges(currentPage, filter)}>
          Refresh
        </Button>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Control
            type="text"
            placeholder="Filter by filename..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Form.Group>
        
        {changes.length === 0 ? (
          <Alert variant="info">No config files found.</Alert>
        ) : (
          <>
            <Table responsive striped hover>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Status</th>
                  <th>Modified on</th>
                  <th>IP Address</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {changes.map((change) => (
                  <tr key={change.id}>
                    <td>
                      <code>{change.filename}</code>
                    </td>
                    <td>{getStatusBadge(change.status)}</td>
                    <td>{formatTimestamp(change.modifiedOn)}</td>
                    <td>
                      {change.ipAddress ? (
                        <code className="text-muted">{change.ipAddress}</code>
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm"
                        onClick={() => handleDownload(change.id, change.filename)}
                      >
                        Download
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {pagination.totalPages > 1 && (
              <div className="d-flex justify-content-center">
                <Pagination>
                  <Pagination.Prev 
                    disabled={currentPage === 1}
                    onClick={() => {
                      const newPage = currentPage - 1;
                      setCurrentPage(newPage);
                      fetchFileChanges(newPage, filter);
                    }}
                  />
                  {Array.from({ length: pagination.totalPages }, (_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === currentPage}
                      onClick={() => {
                        const newPage = index + 1;
                        setCurrentPage(newPage);
                        fetchFileChanges(newPage, filter);
                      }}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next 
                    disabled={currentPage === pagination.totalPages}
                    onClick={() => {
                      const newPage = currentPage + 1;
                      setCurrentPage(newPage);
                      fetchFileChanges(newPage, filter);
                    }}
                  />
                </Pagination>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default FileChanges;